import { createEffect, onCleanup, onMount, Show, type Accessor, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import type { ReauthFrame, ReauthInput, ReauthStatus } from "./api";
import s from "./ReauthModal.module.css";

interface Props {
  open: boolean;
  status: Accessor<ReauthStatus>;
  streamFrames: Accessor<ReauthFrame | null>;
  onInput(msg: ReauthInput): void;
  onClose(): void;
}

const WIDTH = 1280;
const HEIGHT = 800;

function modifiers(e: { shiftKey?: boolean; ctrlKey?: boolean; altKey?: boolean; metaKey?: boolean }): number {
  let m = 0;
  if (e.altKey)   m |= 1;
  if (e.ctrlKey)  m |= 2;
  if (e.metaKey)  m |= 4;
  if (e.shiftKey) m |= 8;
  return m;
}

function buttonName(b: number): "left" | "middle" | "right" {
  return b === 1 ? "middle" : b === 2 ? "right" : "left";
}

export function reauthStatusText(s: ReauthStatus): string {
  switch (s.kind) {
    case "connecting": return "Connecting…";
    case "streaming": return "Streaming";
    case "completed": return "Signed in — session done";
    case "locked": return `${s.driverEmail} is already signing in — try again once they're done`;
    case "unavailable": return "Couldn't start sign-in — reload the page and try again";
    case "ended": return s.reason === "idle-timeout"
      ? "Session timed out — close and click Authenticate again"
      : `Session ended (${s.reason}) — close and click Authenticate again`;
  }
}

export function ReauthModal(props: Props): JSX.Element {
  let canvas: HTMLCanvasElement | undefined;

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!props.open) return;
      if (e.key === "Escape") {
        props.onInput({ type: "abort" });
        props.onClose();
        return;
      }
      // Intercept Ctrl+V / Cmd+V on keydown — read the operator's clipboard
      // here and forward as a paste message. Without this, Ctrl+V would just
      // be sent as a key event to the remote Chromium, which would try to
      // paste from ITS OWN (empty) clipboard.
      if (e.type === "keydown" && (e.ctrlKey || e.metaKey) && (e.code === "KeyV" || e.key === "v" || e.key === "V")) {
        e.preventDefault();
        void navigator.clipboard.readText().then((text) => {
          if (!text) {
            console.warn("[reauth] clipboard returned empty string — nothing to paste");
            return;
          }
          props.onInput({ type: "paste", text });
        }).catch((err) => {
          console.error("[reauth] clipboard.readText failed:", err);
        });
        return;
      }
      const msg: ReauthInput = {
        type: "key",
        event: e.type === "keyup" ? "up" : "down",
        code: e.code,
        key: e.key,
        modifiers: modifiers(e),
      };
      if (e.key.length === 1) msg.text = e.key;
      props.onInput(msg);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    onCleanup(() => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    });
  });

  createEffect(() => {
    const f = props.streamFrames();
    if (!f || !canvas) return;
    if (f.type === "frame") {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, WIDTH, HEIGHT);
      img.src = `data:image/jpeg;base64,${f.b64}`;
    }
  });

  const sendMouse = (event: "press" | "release" | "move", e: MouseEvent) => {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = WIDTH / rect.width;
    const scaleY = HEIGHT / rect.height;
    props.onInput({
      type: "mouse",
      event,
      x: Math.round((e.clientX - rect.left) * scaleX),
      y: Math.round((e.clientY - rect.top) * scaleY),
      button: buttonName(e.button),
      modifiers: modifiers(e),
    });
  };

  return (
    <Show when={props.open}>
      <Portal>
        <div class={s.backdrop} onClick={(e) => { if (e.target === e.currentTarget) props.onClose(); }}>
          <div
            class={s.frame}
            role="dialog"
            aria-modal="true"
            aria-labelledby="reauth-modal-status"
          >
            <canvas
              ref={canvas}
              class={s.canvas}
              width={WIDTH}
              height={HEIGHT}
              tabIndex={0}
              aria-label="Microsoft sign-in screen — interact with mouse and keyboard"
              onMouseDown={(e) => sendMouse("press", e)}
              onMouseUp={(e) => sendMouse("release", e)}
              onMouseMove={(e) => sendMouse("move", e)}
              onWheel={(e) => {
                if (!canvas) return;
                const rect = canvas.getBoundingClientRect();
                props.onInput({ type: "wheel", x: e.clientX - rect.left, y: e.clientY - rect.top, deltaX: e.deltaX, deltaY: e.deltaY });
              }}
            />
            <div id="reauth-modal-status" class={s.status}>{reauthStatusText(props.status())}</div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
